/**
 * Unified Shift Status Styling System
 * Single source of truth for all shift status visual styling
 */

import { ShiftStatus, SHIFT_STATUS_CONFIG } from './shift-status-types';

// Icon mapping for different UI libraries
export type IconType = 'lucide' | 'heroicons' | 'custom';

export interface ShiftStatusIconConfig {
  lucide: string;
  heroicons: string;
  unicode: string;
}

// Comprehensive icon mapping
export const SHIFT_STATUS_ICONS: Record<ShiftStatus, ShiftStatusIconConfig> = {
  [ShiftStatus.PENDING]: {
    lucide: 'Clock',
    heroicons: 'ClockIcon',
    unicode: '⏰'
  },
  [ShiftStatus.IN_PROGRESS]: {
    lucide: 'Play',
    heroicons: 'PlayIcon',
    unicode: '🔄'
  },
  [ShiftStatus.COMPLETED]: {
    lucide: 'CheckCircle',
    heroicons: 'CheckCircleIcon',
    unicode: '✅'
  },
  [ShiftStatus.COMPLETED_LATE]: {
    lucide: 'AlertCircle',
    heroicons: 'ExclamationCircleIcon',
    unicode: '⚠️'
  },
  [ShiftStatus.MISSED]: {
    lucide: 'XCircle',
    heroicons: 'XCircleIcon',
    unicode: '❌'
  },
  [ShiftStatus.UNASSIGNED]: {
    lucide: 'UserX',
    heroicons: 'UserMinusIcon',
    unicode: '👤'
  },
  [ShiftStatus.ON_TIME]: {
    lucide: 'CheckCircle',
    heroicons: 'CheckCircleIcon',
    unicode: '✅'
  },
  [ShiftStatus.LATE]: {
    lucide: 'AlertCircle',
    heroicons: 'ExclamationCircleIcon',
    unicode: '⚠️'
  },
  [ShiftStatus.TOO_EARLY]: {
    lucide: 'Clock',
    heroicons: 'ClockIcon',
    unicode: '⏰'
  },
  [ShiftStatus.LEFT_EARLY]: {
    lucide: 'ArrowRightFromLine',
    heroicons: 'ArrowRightOnRectangleIcon',
    unicode: '➡️'
  },
  [ShiftStatus.OVERTIME]: {
    lucide: 'Clock',
    heroicons: 'ClockIcon',
    unicode: '⏰'
  },
  [ShiftStatus.NO_DEPARTURE_SCAN]: {
    lucide: 'XCircle',
    heroicons: 'XCircleIcon',
    unicode: '❌'
  },
  [ShiftStatus.PLANNING]: {
    lucide: 'Calendar',
    heroicons: 'CalendarIcon',
    unicode: '📅'
  },
  // Composite statuses (arrival + departure combinations)
  [ShiftStatus.TOO_EARLY_OVERTIME]: {
    lucide: 'Clock',
    heroicons: 'ClockIcon',
    unicode: '⏰+'
  },
  [ShiftStatus.TOO_EARLY_LEFT_EARLY]: {
    lucide: 'ArrowRightFromLine',
    heroicons: 'ArrowRightOnRectangleIcon',
    unicode: '⏰➡️'
  },
  [ShiftStatus.LATE_OVERTIME]: {
    lucide: 'Clock',
    heroicons: 'ClockIcon',
    unicode: '⚠️+'
  },
  [ShiftStatus.LATE_LEFT_EARLY]: {
    lucide: 'ArrowRightFromLine',
    heroicons: 'ArrowRightOnRectangleIcon',
    unicode: '⚠️➡️'
  }
};

// Styling variants for different contexts
export enum StyleVariant {
  DEFAULT = 'default',
  COMPACT = 'compact',
  PILL = 'pill',
  OUTLINED = 'outlined',
  MINIMAL = 'minimal',
  CALENDAR = 'calendar'
}

export interface StatusStyleConfig {
  container: string;
  text: string;
  background: string;
  border: string;
  icon?: string;
}

/**
 * Generate style classes for a specific status and variant
 */
export const getShiftStatusStyles = (
  status: ShiftStatus,
  variant: StyleVariant = StyleVariant.COMPACT,
  size: 'sm' | 'md' | 'lg' = 'md',
  showText: boolean = true
): StatusStyleConfig => {
  // Add null safety - use PENDING as fallback for undefined/invalid status
  const config = SHIFT_STATUS_CONFIG[status] || SHIFT_STATUS_CONFIG[ShiftStatus.PENDING];

  // Use badgeColor for badges, regular color for shifts
  const isBadgeVariant = [StyleVariant.COMPACT, StyleVariant.PILL, StyleVariant.MINIMAL, StyleVariant.OUTLINED].includes(variant);
  const colorConfig = isBadgeVariant ? config.badgeColor : config.color;
  
  const baseStyles = {
    background: colorConfig.background,
    text: colorConfig.text,
    border: colorConfig.border
  };

  switch (variant) {
    case StyleVariant.COMPACT:
      return {
        container: `inline-flex items-center gap-0.5 h-6 px-1.5 rounded text-[10px] font-medium justify-center ${baseStyles.background} ${baseStyles.text}`,
        text: baseStyles.text,
        background: baseStyles.background,
        border: baseStyles.border,
        icon: `h-3 w-3 ${baseStyles.text}`
      };

    case StyleVariant.PILL:
      return {
        container: `inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${baseStyles.background} ${baseStyles.text} border ${baseStyles.border}`,
        text: baseStyles.text,
        background: baseStyles.background,
        border: baseStyles.border,
        icon: `h-4 w-4 ${baseStyles.text}`
      };

    case StyleVariant.OUTLINED:
      return {
        container: `inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border-2 ${baseStyles.border} ${baseStyles.text} bg-transparent hover:${baseStyles.background}`,
        text: baseStyles.text,
        background: 'bg-transparent',
        border: baseStyles.border,
        icon: `h-4 w-4 ${baseStyles.text}`
      };

    case StyleVariant.MINIMAL:
      return {
        container: `inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${baseStyles.background} ${baseStyles.text} flex-shrink-0`,
        text: baseStyles.text,
        background: baseStyles.background,
        border: 'border-transparent',
        icon: `h-3 w-3 ${baseStyles.text}`
      };

    case StyleVariant.CALENDAR:
      return {
        container: `w-full h-full flex items-center justify-center rounded text-xs font-medium ${baseStyles.background} ${baseStyles.text} border ${baseStyles.border}`,
        text: `${baseStyles.text} truncate`,
        background: baseStyles.background,
        border: baseStyles.border
      };

    case StyleVariant.DEFAULT:
    default: {
      const sizeClasses = {
        sm: 'px-2 py-1 text-xs gap-1',
        md: 'px-3 py-2 text-sm gap-2',
        lg: 'px-4 py-3 text-base gap-2'
      };

      const iconSizes = {
        sm: 'h-3 w-3',
        md: 'h-4 w-4',
        lg: 'h-5 w-5'
      };

      return {
        container: `inline-flex items-center ${sizeClasses[size]} rounded-lg font-medium ${baseStyles.background} ${baseStyles.text} border ${baseStyles.border}`,
        text: baseStyles.text,
        background: baseStyles.background,
        border: baseStyles.border,
        icon: `${iconSizes[size]} ${baseStyles.text}`
      };
    }
  }
};


/**
 * Get text color classes
 */
export const getShiftStatusTextColor = (status: ShiftStatus): string => {
  // Safety check to prevent undefined access
  if (!status || !SHIFT_STATUS_CONFIG[status]) {
    console.warn('Invalid shift status provided to getShiftStatusTextColor:', status);
    return 'text-gray-700 dark:text-gray-300'; // Default text color
  }
  return SHIFT_STATUS_CONFIG[status].color.text;
};

/**
 * Get border color classes
 */
export const getShiftStatusBorderColor = (status: ShiftStatus): string => {
  // Safety check to prevent undefined access
  if (!status || !SHIFT_STATUS_CONFIG[status]) {
    console.warn('Invalid shift status provided to getShiftStatusBorderColor:', status);
    return 'border-gray-300 dark:border-gray-600'; // Default border color
  }
  return SHIFT_STATUS_CONFIG[status].color.border;
};

/**
 * Get icon name for specific icon library
 */
export const getShiftStatusIcon = (status: ShiftStatus, iconType: IconType = 'lucide'): string => {
  // Safety check to prevent undefined access
  if (!status || !SHIFT_STATUS_ICONS[status]) {
    console.warn('Invalid shift status provided to getShiftStatusIcon:', status);
    return 'Clock'; // Default icon
  }
  return SHIFT_STATUS_ICONS[status][iconType];
};

/**
 * Generate CSS custom properties for status colors
 */
export const generateStatusCSSVariables = (status: ShiftStatus): Record<string, string> => {
  // Safety check to prevent undefined access
  if (!status || !SHIFT_STATUS_CONFIG[status]) {
    console.warn('Invalid shift status provided to generateStatusCSSVariables:', status);
    return {
      '--status-bg': 'bg-gray-100 dark:bg-gray-800',
      '--status-text': 'text-gray-700 dark:text-gray-300',
      '--status-border': 'border-gray-300 dark:border-gray-600'
    };
  }

  const config = SHIFT_STATUS_CONFIG[status];
  return {
    '--status-bg': config.color.background,
    '--status-text': config.color.text,
    '--status-border': config.color.border
  };
};

/**
 * Animation classes for status transitions
 */
export const STATUS_TRANSITION_CLASSES = {
  enter: 'transition-all duration-200 ease-in-out',
  exit: 'transition-all duration-150 ease-in-out',
  pulse: 'animate-pulse',
  bounce: 'animate-bounce'
};

/**
 * Responsive variants for different screen sizes
 */
export const getResponsiveStatusStyles = (
  status: ShiftStatus,
  mobileVariant: StyleVariant = StyleVariant.COMPACT,
  desktopVariant: StyleVariant = StyleVariant.COMPACT
): StatusStyleConfig => {
  const mobileStyles = getShiftStatusStyles(status, mobileVariant, 'sm', true);
  const desktopStyles = getShiftStatusStyles(status, desktopVariant, 'md', true);

  return {
    container: `${mobileStyles.container} md:${desktopStyles.container.replace(/inline-flex.*$/g, '').trim()}`,
    text: `${mobileStyles.text} md:${desktopStyles.text}`,
    background: mobileStyles.background, // Same for both
    border: mobileStyles.border, // Same for both
    icon: `${mobileStyles.icon} md:${desktopStyles.icon}`
  };
};

/**
 * Status-specific hover and focus states
 */
export const getShiftStatusInteractionStyles = (status: ShiftStatus): string => {
  // Safety check to prevent undefined access
  if (!status || !SHIFT_STATUS_CONFIG[status]) {
    console.warn('Invalid shift status provided to getShiftStatusInteractionStyles:', status);
    return 'hover:opacity-80 focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50 focus:ring-gray-300'; // Default focus styles
  }
  const config = SHIFT_STATUS_CONFIG[status];
  return `hover:opacity-80 focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50 ${config.color.border.replace('border-', 'focus:ring-')}`;
};

/**
 * Generate complete style object for React components
 */
export const getShiftStatusStyleObject = (
  status: ShiftStatus,
  variant: StyleVariant = StyleVariant.COMPACT,
  size: 'sm' | 'md' | 'lg' = 'md'
) => {
  const styles = getShiftStatusStyles(status, variant, size, true);

  // Safety check to prevent undefined access
  const config = SHIFT_STATUS_CONFIG[status] || SHIFT_STATUS_CONFIG[ShiftStatus.PENDING];

  return {
    className: styles.container,
    textClassName: styles.text,
    iconClassName: styles.icon,
    backgroundColor: styles.background,
    borderColor: styles.border,
    label: config.label,
    description: config.description,
    iconName: SHIFT_STATUS_ICONS[status]?.lucide || 'Clock',
    requiresAttention: config.requiresAttention,
    isActive: config.isActive,
    cssVariables: generateStatusCSSVariables(status)
  };
};


// Utility function to get all available styles for a status
export const getAllStatusVariantStyles = (status: ShiftStatus) => {
  return Object.values(StyleVariant).reduce((acc, variant) => {
    acc[variant] = getShiftStatusStyles(status, variant, 'md', true);
    return acc;
  }, {} as Record<StyleVariant, StatusStyleConfig>);
};